import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

/*
  Quick setup (plan, stage 1): three questions, then the tracker creates the
  traffic source (from its template), the affiliate network and offer (from
  the network template, with this tracker's postback address), the campaign
  (with the click ID parameter the network expects) and its stream.
*/

const MODES = ['redirect', 'form', 'own'];

function trackingUrlFor(campaign) {
  if (!campaign?.tracking_id) return '';
  let base = String(API || '').replace(/\/api\/?$/, '');
  if (!/^https?:\/\//i.test(base)) base = `${window.location.origin}${base}`;
  return `${base}/r/${campaign.tracking_id}`;
}

function filterList(items, query) {
  const q = query.trim().toLowerCase();
  const list = q ? items.filter((it) => String(it.name || '').toLowerCase().includes(q)) : items;
  return list.slice(0, 60);
}

export default function QuickSetup({ open, onOpenChange, onCheck }) {
  const { t } = useT();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('redirect');
  const [sourceTpls, setSourceTpls] = useState([]);
  const [netTpls, setNetTpls] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [sourceQuery, setSourceQuery] = useState('');
  const [netQuery, setNetQuery] = useState('');
  const [source, setSource] = useState(null);
  const [net, setNet] = useState(null);
  const [integrationId, setIntegrationId] = useState('');
  const [offerName, setOfferName] = useState('');
  const [offerUrl, setOfferUrl] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setResult(null);
    axios.get(`${API}/traffic-sources/templates`).then((r) => setSourceTpls(r.data?.templates || [])).catch(() => {});
    axios.get(`${API}/affiliate-networks/templates`).then((r) => setNetTpls(r.data?.templates || [])).catch(() => {});
    axios.get(`${API}/integrations`).then((r) => setIntegrations(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [open]);

  const sources = useMemo(() => filterList(sourceTpls, sourceQuery), [sourceTpls, sourceQuery]);
  const nets = useMemo(() => filterList(netTpls, netQuery), [netTpls, netQuery]);
  const urlOk = /^https?:\/\/\S+$/i.test(offerUrl.trim());

  const modeLabel = (m) => {
    if (m === 'redirect') return t('The visitor goes to the partner site by a link (affiliate network)');
    if (m === 'form') return t('The visitor fills a form, and the lead goes to the partner');
    return t('Your own site or shop');
  };

  const finish = async () => {
    if (!urlOk) {
      toast.error(t('Enter a full link starting with http:// or https://.'));
      return;
    }
    setBusy(true);
    try {
      const src = await axios.post(`${API}/traffic-sources`, {
        name: source?.name || t('Traffic source'),
        template_name: source?.slug || null,
        parameters: source?.parameters || {},
        postback_url: (source?.outbound_postback && source.outbound_postback.url) || null,
      });
      let networkId = null;
      if (mode === 'redirect' && net) {
        const created = await axios.post(`${API}/affiliate-networks`, {
          name: net.name,
          vertical: net.vertical || null,
          template_slug: net.slug,
          postback_url_template: net.url_template || null,
          offer_params: net.offer_params || null,
          status_mapping: net.status_mapping || null,
        });
        networkId = created.data?.id || null;
      }
      const name = offerName.trim() || net?.name || t('Offer');
      const offer = await axios.post(`${API}/offers`, {
        name,
        url: offerUrl.trim(),
        affiliate_network_id: networkId,
        ...(mode === 'form' && integrationId
          ? { integration_routes: [{ integration_id: integrationId, params: {}, weight: 100, is_active: true }] }
          : {}),
      });
      // The click ID rides on the offer link under the name the network expects.
      const netParam = (net?.offer_params || []).find((p) => p && p.placeholder === '{click_id}');
      const clickParam = (mode === 'redirect' && netParam && netParam.key) || 'click_id';
      const campaign = await axios.post(`${API}/campaigns`, {
        name: campaignName.trim() || `${src.data?.name || ''} — ${name}`,
        target_url: offerUrl.trim(),
        traffic_source_id: src.data?.id || null,
        click_id_param: clickParam,
      });
      const offerId = offer.data?.id;
      await axios.post(`${API}/campaigns/${campaign.data.id}/streams`, {
        name,
        weight: 100,
        offer_ids: offerId ? [offerId] : [],
        offer_weights: offerId ? { [offerId]: 100 } : {},
      });
      setResult(campaign.data);
    } catch (err) {
      toast.error(t('Could not finish the setup: {error}', { error: err?.response?.data?.detail || err?.message || '' }));
    } finally {
      setBusy(false);
    }
  };

  const link = result ? trackingUrlFor(result) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('Quick setup')}</DialogTitle>
          <DialogDescription>
            {t('Answer three questions: the tracker creates the source, the offer, the campaign and its stream for you.')}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 text-sm" data-testid="quick-setup-done">
            <p className="flex items-start gap-2 font-medium text-primary">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              {t('Done: the campaign and its stream are created. Put this link in your ad:')}
            </p>
            <div className="flex items-start gap-2">
              <code className="flex-1 break-all rounded bg-muted p-2 text-xs">{link}</code>
              <Button
                size="sm"
                variant="outline"
                aria-label={t('Copy')}
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(link);
                    toast.success(t('Copied'));
                  } catch (_e) {
                    /* clipboard blocked: the link stays visible */
                  }
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            {mode === 'own' ? (
              <p className="text-muted-foreground">
                {t('Your own site: the click ID comes to the page as click_id; send it back to the tracker with the conversion.')}
              </p>
            ) : null}
            <div className="flex justify-end">
              <Button onClick={() => onCheck && onCheck(result.id)}>{t('Check click and conversion')}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-sm" data-testid="quick-setup">
            {step === 1 ? (
              <section className="space-y-2">
                <div className="font-medium">{t('1. How do you get conversions?')}</div>
                {MODES.map((m) => (
                  <label key={m} className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2">
                    <input type="radio" name="qs-mode" checked={mode === m} onChange={() => setMode(m)} className="mt-1" />
                    <span>{modeLabel(m)}</span>
                  </label>
                ))}
              </section>
            ) : null}

            {step === 2 ? (
              <section className="space-y-2">
                <div className="font-medium">{t('2. Where will the traffic come from?')}</div>
                <Input value={sourceQuery} onChange={(e) => setSourceQuery(e.target.value)} placeholder={t('Search')} />
                <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setSource(null)}
                    className={`rounded-md border px-2 py-1 text-left ${source === null ? 'border-primary' : 'border-border'}`}
                  >
                    {t('Other source (no template)')}
                  </button>
                  {sources.map((s) => (
                    <button
                      key={s.slug}
                      type="button"
                      onClick={() => setSource(s)}
                      className={`rounded-md border px-2 py-1 text-left ${source?.slug === s.slug ? 'border-primary' : 'border-border'}`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {step === 3 ? (
              <section className="space-y-3">
                <div className="font-medium">{t('3. Where should visitors go?')}</div>
                {mode === 'redirect' ? (
                  <div className="space-y-2">
                    <Label>{t('Affiliate network')}</Label>
                    <Input value={netQuery} onChange={(e) => setNetQuery(e.target.value)} placeholder={t('Search')} />
                    <div className="grid max-h-44 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
                      {nets.map((n) => (
                        <button
                          key={n.slug}
                          type="button"
                          onClick={() => setNet(n)}
                          className={`rounded-md border px-2 py-1 text-left ${net?.slug === n.slug ? 'border-primary' : 'border-border'}`}
                        >
                          {n.name}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('The postback address and the click ID parameter are filled in from the network template.')}
                    </p>
                  </div>
                ) : null}
                {mode === 'form' ? (
                  <div className="space-y-2">
                    <Label>{t('Partner integration')}</Label>
                    {integrations.length ? (
                      <select
                        className="w-full rounded-md border border-border bg-background px-2 py-2"
                        value={integrationId}
                        onChange={(e) => setIntegrationId(e.target.value)}
                      >
                        <option value="">—</option>
                        {integrations.map((it) => (
                          <option key={it.id} value={it.id}>{`${it.name} (${it.type})`}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {t('No integrations yet — add one in Integrations, then come back.')}
                      </p>
                    )}
                  </div>
                ) : null}
                <div className="space-y-1">
                  <Label>{t('Offer link from the network (or your page)')}</Label>
                  <Input value={offerUrl} onChange={(e) => setOfferUrl(e.target.value)} placeholder="https://" />
                </div>
                <div className="space-y-1">
                  <Label>{t('Offer name')}</Label>
                  <Input value={offerName} onChange={(e) => setOfferName(e.target.value)} placeholder={net?.name || ''} />
                </div>
              </section>
            ) : null}

            {step === 4 ? (
              <section className="space-y-2">
                <div className="font-medium">{t('4. Campaign name')}</div>
                <Input
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder={`${source?.name || t('Traffic source')} — ${offerName || net?.name || t('Offer')}`}
                />
              </section>
            ) : null}

            <div className="flex justify-between gap-2">
              <Button variant="outline" disabled={step === 1 || busy} onClick={() => setStep((s) => Math.max(1, s - 1))}>
                {t('Back')}
              </Button>
              {step < 4 ? (
                <Button
                  disabled={step === 3 && (!urlOk || (mode === 'redirect' && !net))}
                  onClick={() => setStep((s) => Math.min(4, s + 1))}
                >
                  {t('Next')}
                </Button>
              ) : (
                <Button disabled={busy || !urlOk} onClick={finish}>
                  {t('Create')}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
